import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  Loader2, Plus, Trash2, FileBadge, AlertTriangle, ExternalLink, Pencil,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_TYPES = [
  { value: "business",       label: "Business License" },
  { value: "software",       label: "Software / Reseller" },
  { value: "tax",            label: "Tax / Sales Permit" },
  { value: "data_privacy",   label: "Data Privacy (GDPR/CCPA)" },
  { value: "professional",   label: "Professional Cert" },
  { value: "other",          label: "Other" },
];

const STATUSES = [
  { value: "active",       label: "Active",        color: "bg-emerald-100 text-emerald-700" },
  { value: "renewal_due",  label: "Renewal Due",   color: "bg-amber-100 text-amber-700" },
  { value: "pending",      label: "Pending",       color: "bg-blue-100 text-blue-700" },
  { value: "expired",      label: "Expired",       color: "bg-rose-100 text-rose-700" },
];

const EMPTY = {
  name: "", license_type: "business", issuing_authority: "", license_number: "",
  jurisdiction: "", issue_date: "", expiry_date: "", renewal_cost: "",
  status: "active", document_url: "", notes: "",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";
const daysUntil = (s) => {
  if (!s) return null;
  const ms = new Date(s).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export default function LicensesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/licenses`);
      setItems(res.data?.licenses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load licenses");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (lic) => {
    setForm({
      name: lic.name || "", license_type: lic.license_type || "business",
      issuing_authority: lic.issuing_authority || "", license_number: lic.license_number || "",
      jurisdiction: lic.jurisdiction || "",
      issue_date: lic.issue_date || "", expiry_date: lic.expiry_date || "",
      renewal_cost: lic.renewal_cost ?? "", status: lic.status || "active",
      document_url: lic.document_url || "", notes: lic.notes || "",
    });
    setEditingId(lic.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.renewal_cost === "") delete payload.renewal_cost;
      if (editingId) {
        await axios.put(`${API}/owner-console/licenses/${editingId}`, payload);
        toast.success("License updated");
      } else {
        await axios.post(`${API}/owner-console/licenses`, payload);
        toast.success("License added");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this license record? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/owner-console/licenses/${id}`);
      toast.success("License deleted");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const expiringSoon = items.filter(i => {
    const d = daysUntil(i.expiry_date);
    return d !== null && d >= 0 && d <= 60;
  });
  const expired = items.filter(i => {
    const d = daysUntil(i.expiry_date);
    return d !== null && d < 0;
  });

  return (
    <div className="space-y-4" data-testid="licenses-panel">
      <Card className="border-amber-200/40">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileBadge className="h-5 w-5 text-amber-600" />
                Licenses & Permits
              </CardTitle>
              <CardDescription>
                Business licenses, tax permits, data-privacy registrations, and regulatory certs you operate under.
              </CardDescription>
            </div>
            <Button onClick={openNew} data-testid="add-license-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add License
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(expired.length > 0 || expiringSoon.length > 0) && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {expired.length > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-md border border-rose-200 bg-rose-50/60 text-rose-900 text-sm" data-testid="licenses-expired-banner">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span><strong>{expired.length}</strong> expired license{expired.length > 1 ? "s" : ""} — renew or remove.</span>
                </div>
              )}
              {expiringSoon.length > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-md border border-amber-200 bg-amber-50/60 text-amber-900 text-sm" data-testid="licenses-expiring-banner">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span><strong>{expiringSoon.length}</strong> expiring within 60 days.</span>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No licenses tracked yet. Click <strong>Add License</strong> to begin.
              <div className="mt-2 text-xs">
                Recommended starter set: Federal/State Business License, Sales Tax Permit, GDPR Data Processor Registration (if you serve EU customers), Stripe Tax Activation Confirmation.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(lic => {
                const d = daysUntil(lic.expiry_date);
                const statusInfo = STATUSES.find(s => s.value === lic.status) || STATUSES[0];
                return (
                  <div key={lic.id} className="p-3 rounded-md border bg-card hover:bg-accent/30 transition-colors" data-testid={`license-row-${lic.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{lic.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {LICENSE_TYPES.find(t => t.value === lic.license_type)?.label || lic.license_type}
                          </Badge>
                          <Badge className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
                          {d !== null && d < 0 && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                          {d !== null && d >= 0 && d <= 60 && <Badge className="bg-amber-100 text-amber-800 text-xs">Expires in {d}d</Badge>}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground space-x-2">
                          {lic.issuing_authority && <span>{lic.issuing_authority}</span>}
                          {lic.jurisdiction && <span>· {lic.jurisdiction}</span>}
                          {lic.license_number && <span>· #{lic.license_number}</span>}
                          <span>· Expires {fmtDate(lic.expiry_date)}</span>
                          {lic.renewal_cost != null && <span>· ${Number(lic.renewal_cost).toFixed(2)} renewal</span>}
                        </div>
                        {lic.notes && <div className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{lic.notes}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        {lic.document_url && (
                          <a href={lic.document_url} target="_blank" rel="noopener noreferrer">
                            <Button size="icon" variant="ghost" title="Open document"><ExternalLink className="h-4 w-4" /></Button>
                          </a>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => openEdit(lic)} data-testid={`edit-license-${lic.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(lic.id)} data-testid={`delete-license-${lic.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit License" : "Add License / Permit"}</DialogTitle>
            <DialogDescription>Track issue dates, expiry, renewal costs, and link to the actual document.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Federal Business License" data-testid="license-name-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.license_type} onValueChange={(v) => setForm({ ...form, license_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LICENSE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Issuing Authority</Label>
              <Input value={form.issuing_authority} onChange={e => setForm({ ...form, issuing_authority: e.target.value })} placeholder="Government of Canada / IRS / State of CA" />
            </div>
            <div>
              <Label>Jurisdiction</Label>
              <Input value={form.jurisdiction} onChange={e => setForm({ ...form, jurisdiction: e.target.value })} placeholder="Country / State / Province" />
            </div>
            <div>
              <Label>License Number</Label>
              <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} />
            </div>
            <div>
              <Label>Renewal Cost ($)</Label>
              <Input type="number" step="0.01" value={form.renewal_cost} onChange={e => setForm({ ...form, renewal_cost: e.target.value })} />
            </div>
            <div>
              <Label>Issue Date</Label>
              <Input type="date" value={form.issue_date || ""} onChange={e => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Document URL (object storage / drive link)</Label>
              <Input value={form.document_url} onChange={e => setForm({ ...form, document_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Renewal portal link, contact info, conditions, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-license-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {editingId ? "Update" : "Save License"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
